/* global window */
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Header, Footer, DataTable, Search } from '@3plearning/react-components';
import ViewIcon from '../../assets/images/view-icon.svg';
import css from './class-details.scss';
import modalActions from '../../components/modal/modal-actions';
import classDetailsActions, { CLASS_DETAILS_DATA_RETRIEVED } from './class-details-actions';
import { NO_STUDENTS_FOUND_LOAD, NO_STUDENTS_FOUND_SEARCH } from './class-details-constants';
import datePickerActions from '../../components/date-picker/date-picker-actions';
import Resources from '../../helpers/resources';
import UrlHelper from '../../helpers/url-helper';
import selectedStudentActions from './selected-student-actions';
import { DatePicker } from '../../components/date-picker/date-picker';
import { DEFAULT_DATE_OPTIONS_OBJ }
    from '../../components/date-picker/components/date-picker-trans/date-picker-constants';
import {
    getConfig,
    locale
} from '../../locale/locale-utils';
import CustomButton from '../../components/button';
import {
    calculateClassTotal,
    CERTIFICATE_DOWNLOAD_LIMIT,
    exportStudentCertificates
} from '../../helpers/utils';
import { ALL_NEW, FULL_COLOR, PRINT_FRIENDLY } from '../certificates/download-constants';
import DownloadIcon from '../../assets/images/download-icon.svg';
import PopUpMenu from '../overview/sub-components/popup-menu';
import downloadActions from '../certificates/download-certificates-actions';
import { ToastContainer } from '../../components/toast/toast-container';
import participationReportActions from '../participation-report/participation-report-actions';

/**
 * Class Details Redux component
 * Receives data via 'props'
 * Base component for Class Details page, displaying data table, search bar, date picker, etc.
 */
export class ClassDetails extends Component {
    static contextTypes = {
        router: PropTypes.object
    };

    static propTypes = {
        selectedClassroom: PropTypes.shape({
            id: PropTypes.number,
            class: PropTypes.string
        }),
        getClassDetailsData: PropTypes.func,
        selectedDateRange: PropTypes.shape({
            type: PropTypes.string,
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }),
        classDetailsData: PropTypes.array,
        openModal: PropTypes.func,
        updateClassDetailsDateRange: PropTypes.func,
        productId: PropTypes.string,
        academicYear: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }),
        schoolId: PropTypes.number,
        localeData: PropTypes.object,
        selectStudent: PropTypes.func,
        downloadAllSchoolOrClassCerts: PropTypes.func,
        downloadCertificatesState: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {
            searchFieldText: '',
            classDetailsData: [],
            options: DEFAULT_DATE_OPTIONS_OBJ,
            newCertExpanded: false,
            pastCertExpanded: false,
            shouldDisplayDropdown: false,
            showToast: false,
            dropdownTriggered: false
        };
    }

    componentDidMount() {
        const { history } = this.context.router;
        const { selectedDateRange, selectedClassroom } = this.props;
        const { startDate, endDate } = selectedDateRange;

        if (!selectedClassroom || !startDate) {
            history.push('/home');
        } else {
            this.getClassDetailsData(startDate, endDate);
        }
        document.addEventListener('click', this.handleOutsideClick);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
    }

    componentWillReceiveProps(nextProps) {
        const { selectedDateRange } = this.props;

        if (selectedDateRange !== nextProps.selectedDateRange) {
            const { startDate, endDate } = nextProps.selectedDateRange;
            this.getClassDetailsData(startDate, endDate);
        }
    }

    getClassDetailsData(startDate, endDate) {
        const classroomId = this.props.selectedClassroom.id;
        this.props.getClassDetailsData(classroomId, startDate, endDate)
            .then((action) => {
                if (action.type === CLASS_DETAILS_DATA_RETRIEVED) {
                    this.setClassDetailsData(action.payload);
                } else {
                    this.displayError(action.error);
                }
            }, error => this.displayError(error));
    }

    setClassDetailsData(classDetailsData, filterText = this.state.filterText) {
        let emptyDataText = NO_STUDENTS_FOUND_LOAD;
        if (filterText) {
            classDetailsData = this.filterStudents(classDetailsData, filterText);
            emptyDataText = NO_STUDENTS_FOUND_SEARCH;
        }
        classDetailsData = this.addViewIcons(classDetailsData);
        classDetailsData.emptyDataText = emptyDataText;
        const { newCertExpanded, pastCertExpanded } = this.state;
        const { localeData } = this.props;
        const tableConfig = getConfig({
            newCertExpanded,
            pastCertExpanded,
            localeData,
            callback: this.manageCertificateTypeDisplay,
            emptyDataText
        });
        this.setState({ classDetailsData, searchFieldText: filterText, tableConfig });
    }

    filterStudents = (data, filterText) => {
        filterText = filterText.toLowerCase();
        return data.filter(c => c.student.toLowerCase().indexOf(filterText) > -1);
    };

    /**
     * Updates data in class details array, adding view icons to participation points and total certs fields
     */
    addViewIcons(classDetailsData) {
        return classDetailsData.map(item => ({
            ...item,
            participationPoints: this.renderTextWithViewIcon(
                'participationPoints', item, () => this.loadParticipationReport(item)),
            totalCertificates: this.renderTextWithViewIcon(
                'totalCertificates', item, () => this.loadCertificatesPage(item))
        }));
    }

    /**
     * Loads participation report for a particular student in a new tab
     */
    loadParticipationReport(studentData) {
        const { selectedClassroom, selectedDateRange, productId, academicYear, schoolId } = this.props;
        const authToken = Resources.env.authToken;

        const queryParams = [
            `profileId=${studentData.id}`,
            `studentName=${studentData.student}`,
            `schoolId=${schoolId}`,
            `classroomId=${selectedClassroom.id}`,
            `startDate=${selectedDateRange.startDate}`,
            `endDate=${selectedDateRange.endDate}`,
            `academicYear=${academicYear.year}`,
            `productId=${productId}`,
            `authToken=${authToken}`,
            `locale=${locale()}`
        ];

        let url = '';
        if (UrlHelper.isRunningOnDeveloperMachine()) {
            queryParams.push(`env=${Resources.env.name}`);
        } else {
            // eslint-disable-next-line no-underscore-dangle
            const appConfig = window._env_;
            url = appConfig ? appConfig.ReportsUrl : '';
        }

        url += `/participation-report?${queryParams.join('&')}`;

        window.open(encodeURI(url), '_blank');
    }

    loadCertificatesPage(student) {
        const { history } = this.context.router;

        this.props.selectStudent(student);
        history.push('/certificates');
    }

    onBackClick = () => {
        const { history } = this.context.router;
        history.goBack();
    };

    displayError = (error) => {
        const { localeData } = this.props;
        this.props.openModal({
            title: localeData['Modal - Oops'],
            content: <p>{error}</p>,
            buttons: [{ title: localeData['Modal - TryAgain'], primary: true }]
        });
    };

    /**
     * Renders td content, adding view icon button to right hand side
     */
    renderTextWithViewIcon = (fieldName, studentData, clickHandler) => (
        <div className={`${fieldName} ${css['icon-container']}`}>
            <div className={css.text}>{studentData[fieldName]}</div>
            <Button
                icon={ViewIcon}
                primary
                type={Button.LOZENGE}
                className={css.icon}
                onClick={clickHandler}
            />
        </div>
    );
    /**
     * Manage new and old certificates display
     * */
    manageCertificateTypeDisplay = (colSelected) => {
        colSelected.expanded = !colSelected.expanded;
        const isNewCertType = colSelected.certType === 'new';
        const { newCertExpanded, pastCertExpanded } = this.state;
        const newCertExpand = isNewCertType ? colSelected.expanded : newCertExpanded;
        const pastCertExpand = !isNewCertType ? colSelected.expanded : pastCertExpanded;
        this.setTableConfig({ newCertExpanded: newCertExpand, pastCertExpanded: pastCertExpand });
    }


    setTableConfig({ newCertExpanded, pastCertExpanded }) {
        const { classDetailsData: { emptyDataText } } = this.state;
        const { localeData } = this.props;
        const tableConfig = getConfig({
            newCertExpanded,
            pastCertExpanded,
            localeData,
            callback: this.manageCertificateTypeDisplay,
            emptyDataText
        });
        this.setState({ tableConfig, newCertExpanded, pastCertExpanded });
    }

    showPrintModal = ({
        total,
        selectedDateRange,
        localeData,
        classroomId,
        downloadAllSchoolOrClassCerts,
        searchFieldText
    }) => {
        this.props.openModal({
            title: `${total} ${total === 1 ? localeData['Label - Certificate'] : localeData['Label - Certificates']}`,
            content: `${localeData['PrintModal - SelectVersion']}`,
            certificatesCount: total,
            buttons: [
                {
                    title: `${localeData['Label - FullColour']}`,
                    primary: true,
                    icon: DownloadIcon,
                    btnType: FULL_COLOR,
                    handler: () => downloadAllSchoolOrClassCerts({
                        fullColor: true,
                        classroomId,
                        filterText: searchFieldText,
                        startDate: selectedDateRange.startDate,
                        endDate: selectedDateRange.endDate,
                        downloadType: FULL_COLOR
                    })

                },
                {
                    title: `${localeData['Label - PrintFriendly']}`,
                    primary: true,
                    icon: DownloadIcon,
                    btnType: PRINT_FRIENDLY,
                    handler: () => downloadAllSchoolOrClassCerts({
                        fullColor: false,
                        classroomId,
                        filterText: searchFieldText,
                        startDate: selectedDateRange.startDate,
                        endDate: selectedDateRange.endDate,
                        downloadType: PRINT_FRIENDLY
                    })
                }],
            useNewModal: true
        });
    }

    downloadCertificateOrReports = (event) => {
        const { classDetailsData, searchFieldText } = this.state;
        const {
            selectedClassroom,
            selectedDateRange,
            localeData,
            downloadAllSchoolOrClassCerts
        } = this.props;
        const totalCertificates = calculateClassTotal(classDetailsData);

        this.setState({
            ...this.state,
            shouldDisplayDropdown: false
        });

        if (event.target.id === '0') {
            if (totalCertificates > CERTIFICATE_DOWNLOAD_LIMIT) {
                this.setState({
                    ...this.state,
                    shouldDisplayDropdown: false,
                    showToast: true
                });
            } else {
                this.showPrintModal({
                    total: totalCertificates,
                    selectedDateRange,
                    localeData,
                    classroomId: selectedClassroom.id,
                    searchFieldText,
                    downloadAllSchoolOrClassCerts
                });
            }
        }
        // TODO uncomment this when participation report is needed
        // else {
        //     downloadParticipationReport({
        //         paramData: {
        //             locale: locale(),
        //             classroomId: selectedClassroom.id,
        //             startDate: selectedDateRange.startDate,
        //             endDate: selectedDateRange.endDate,
        //             nameFilter: searchFieldText
        //         }
        //     });
        // }

        const showToastTimer = setTimeout(() => {
            this.setState({
                ...this.state,
                showToast: false
            });
            clearTimeout(showToastTimer);
        }, 3000);
    }

    handleOutsideClick = (event) => {
        if (this.state.shouldDisplayDropdown && !this.state.dropdownTriggered) {
            this.setState({ shouldDisplayDropdown: false });
            event.stopPropagation();
        }
        this.setState({
            dropdownTriggered: false
        });
    };

    render() {
        const { classDetailsData, tableConfig, shouldDisplayDropdown, showToast } = this.state;
        const {
            selectedClassroom,
            academicYear,
            selectedDateRange,
            updateClassDetailsDateRange,
            localeData,
            downloadCertificatesState
        } = this.props;
        if (!selectedClassroom) {
            return null;
        }
        const isDownloadActive = downloadCertificatesState.isActive;
        const totalCertificatesInAllClasses = calculateClassTotal(classDetailsData);
        const downloadDisabled = totalCertificatesInAllClasses === 0;
        const options = [
            { label: 'certificate', value: localeData['DropDown - All Certificates'] }
            // TODO uncomment when participation option is needed
            // { label: 'participation', value: localeData['DropDown - All Participation & awards reports'] }
        ];
        const toastData = {
            messageData: {
                message: localeData['Toast - Too Many Certificate Error Message'],
                type: 'fail',
                hasClose: true
            }
        };

        return (
            <div className={css.component}>
                <Header title={localeData['Header - ClassDetails']} />
                <div className={css['main-content']}>
                    <div className={css.header}>
                        <h2>{selectedClassroom.class}</h2>

                        <div className={css['filter-container']}>

                            {showToast && (
                                <ToastContainer toastData={toastData} />
                            )}

                            <div>
                                <CustomButton
                                    className={css['export-button']}
                                    text={localeData['Label - Export']}
                                    primary={false}
                                    type={CustomButton.LOZENGE}
                                    size={CustomButton.LARGE}
                                    disabled={downloadDisabled}
                                    onClick={() => {
                                        // eslint-disable-next-line max-len
                                        exportStudentCertificates(selectedClassroom.id, this.state.searchFieldText, selectedDateRange);
                                    }}
                                />
                                <CustomButton
                                    className={css['download-button']}
                                    text={
                                        isDownloadActive ?
                                            localeData['Label - Preparing download'] : localeData['Label - Download']
                                    }
                                    primary={false}
                                    type={CustomButton.LOZENGE}
                                    size={CustomButton.SMALL}
                                    icon={isDownloadActive ? null : DownloadIcon}
                                    alignIcon={CustomButton.RIGHT}
                                    btnType={ALL_NEW}
                                    disabled={isDownloadActive}
                                    onClick={() => {
                                        this.setState({
                                            ...this.state,
                                            shouldDisplayDropdown: !this.state.shouldDisplayDropdown,
                                            dropdownTriggered: !this.state.shouldDisplayDropdown
                                        });
                                    }}
                                />
                            </div>

                            {shouldDisplayDropdown && (
                                <PopUpMenu
                                    downloadDisabled={downloadDisabled}
                                    isClassLevel
                                    options={options}
                                    downloadCertificateOrReports={this.downloadCertificateOrReports}
                                />
                            )}

                            <div>
                                <Search
                                    onType={(filterText) => {
                                        this.setClassDetailsData(this.props.classDetailsData, filterText);
                                    }}
                                    placeholder={localeData['Label- StudentName']}
                                />
                            </div>

                            <div>
                                <DatePicker
                                    currentDate={selectedDateRange}
                                    academicYear={academicYear}
                                    onDateChange={updateClassDetailsDateRange}
                                    localeData={localeData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={css['table-container']}>
                        <DataTable
                            className={css['data-table']}
                            config={tableConfig}
                            data={classDetailsData}
                        />
                    </div>
                </div>
                <Footer>
                    <Button
                        text={localeData['Label - Back']}
                        type={Button.LOZENGE}
                        size={Button.LARGE}
                        primary
                        onClick={this.onBackClick}
                    />
                </Footer>
            </div>
        );
    }
}

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from CombinedReducers.
 */
function mapStateToProps(state) {
    return {
        localeData: state.localeData,
        selectedClassroom: state.selectedClass,
        selectedDateRange: state.selectedDateRange,
        classDetailsData: state.classDetailsData,
        productId: state.productId,
        academicYear: state.academicYear,
        schoolId: state.schoolId,
        downloadCertificatesState: state.downloadCertificatesState
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getClassDetailsData: classDetailsActions.getClassDetailsData,
        openModal: modalActions.openModal,
        updateClassDetailsDateRange: datePickerActions.updateSelectedDateRange,
        selectStudent: selectedStudentActions.selectStudent,
        downloadAllSchoolOrClassCerts: downloadActions.downloadAllSchoolOrClassCerts,
        downloadParticipationReport: participationReportActions.downloadParticipationReport
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ClassDetails);



// WEBPACK FOOTER //
// ./containers/class-details/class-details.js